import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import dayjs from 'dayjs'
import * as echarts from 'echarts'
import { getLonStakedAmounts } from '~/apis'
import { useBreakpoint } from '~/hooks'
import { useWeb3React } from '@web3-react/core'
import { formatNumber } from '~/utils/numbers'
import { StyledChartsContainer } from './styled'
import styled from 'styled-components'

type ChartData = {
  name: number
  value: [number, string]
}[]

const CHART_COLOR = '#bd89f0'

const LonStakingChart: React.FC = () => {
  const { t } = useTranslation()
  const { chainId } = useWeb3React()
  const { isMobile } = useBreakpoint()
  const chartRef = useRef(null)
  let chartInstance = null

  const { data: stakedAmounts } = useSWR(['getLonStakedAmounts', chainId], () =>
    getLonStakedAmounts(),
  )
  const chartData = stakedAmounts?.map((item) => {
    return {
      name: item.date * 1000,
      value: [item.date * 1000, item.dailyTotalStakedAmount],
    }
  }) as ChartData
  const latestItem = chartData?.[chartData?.length - 1]?.value

  const canvasSize = isMobile
    ? { width: '100%', height: 210 }
    : {
        width: 520,
        height: 280,
      }

  const getOption = () => ({
    grid: {
      top: '20%',
      bottom: 0,
      left: isMobile ? '10%' : '5%',
      right: 0,
      containLabel: true,
    },
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const data = params[0].value
        return [
          `<span style="font-size: 13px;color: ${CHART_COLOR};font-weight: bold;">${formatNumber(
            data[1],
            2,
          )} LON</span>`,
          `<span style="font-size: 13px;color: #9598ab;">${dayjs(
            data[0],
          ).format('MMM DD.YYYY')}</span>`,
        ].join('<br/>')
      },
    },
    xAxis: {
      type: 'time',
      splitNumber: 2,
      axisLabel: {
        formatter: '{MMM} {dd}',
        showMinLabel: true,
        showMaxLabel: true,
        fontSize: isMobile ? 11 : 12,
      },
    },
    yAxis: {
      type: 'value',
      position: 'right',
      splitLine: {
        show: false,
      },
      axisLabel: {
        formatter: (value) => {
          if (value >= 1000000) {
            return (value / 1000000).toFixed(0) + 'M'
          } else if (value >= 1000) {
            return (value / 1000).toFixed(0) + 'K'
          }
          return value
        },
        fontSize: isMobile ? 11 : 12,
      },
    },
    series: [
      {
        type: 'line',
        data: chartData,
        itemStyle: {
          color: CHART_COLOR,
        },
        symbol: 'none',
      },
    ],
  })

  useEffect(() => {
    if (chartRef.current) {
      chartInstance = echarts.init(chartRef.current)
      chartInstance.setOption(getOption())
    }

    const handleResize = () => chartInstance?.resize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance?.dispose()
    }
  }, [chartData])

  return (
    <StyledChartsContainer className="lon-total-staked-amount">
      <div className="header">
        <p className="title">{t('staking_total_lon_staked')}</p>
      </div>
      {latestItem && (
        <StyledLatestItem>
          <StyledAmount>
            <StyledDot />
            {`${formatNumber(latestItem[1], 2)} LON`}
          </StyledAmount>
          <StyledDate>{dayjs(latestItem[0]).format('MMM DD.YYYY')}</StyledDate>
        </StyledLatestItem>
      )}
      <div ref={chartRef} style={canvasSize} />
    </StyledChartsContainer>
  )
}

export default LonStakingChart

const StyledLatestItem = styled.div`
  position: absolute;
  top: 80px;
  left: 36px;

  ${({ theme }) => theme.media.sm`
    top: 55px;
    left: 10px;
  `}
`

const StyledAmount = styled.span`
  display: flex;
  align-items: center;
  font-size: 13px;
  line-height: 21px;
  color: ${CHART_COLOR};
  font-weight: bold;
`

const StyledDot = styled.span`
  display: inline-block;
  border-radius: 50%;
  width: 7px;
  height: 7px;
  margin-right: 8px;
  background: ${CHART_COLOR};
`

const StyledDate = styled.span`
  font-size: 13px;
  color: #9598ab;
  padding-left: 15px;
`
